/**
 * 정정폼(본인 아님) — 화면단위 모듈 + BottomSheet 전환 (Phase2-D).
 *   makeNotmeForm({ S, go, setActiveForm }) → { openSheet, rerender }
 *
 * 정정 요청서(이름·필요서류 정적안내·증빙·사유·동의·제출)를 BS 로 노출. (옛 SomyungForm 과 같은 구조였고, 소명 폼은 2026-10 폐지)
 * 체불내역/헤더는 base(auth_first)에 있어 중복 제거. 제출확인/완료는 "같은 시트 내용 교체"(BS 싱글톤).
 *   go/S/setActiveForm 주입, showToast/showOv/showLoading/openPhotoUpload 는 UiHelpers, phoneNumber 는 전역.
 */
import { showToast, showOv, showLoading, openPhotoUpload, openPrivacyConsent } from './UiHelpers';
import Util from '../../../Common/Util';

export function makeNotmeForm({ S, go, setActiveForm }) {
    let _sheet = null;

    // 제출 버튼 활성화 — 이름2+ / 사유10+ / 증빙1+ / 동의
    function updateBtn() {
        const nameOk   = (S.userName || '').trim().length >= 2;
        const reasonOk = (S.notmeReason || '').trim().length >= 10;
        const photoOk  = S.notmePhotos.length > 0;
        const ok = nameOk && reasonOk && photoOk && S.agreedPrivacy;
        $('#nm-submit').prop('disabled', !ok).css({
            background: ok ? 'var(--grad)' : 'var(--bdr2)',
            cursor:     ok ? 'pointer' : 'not-allowed',
        });
    }

    // 폼 본문(BS content) — rNotMe 의 '정정 요청서' 박스. 루트(.nm-form-root)로 감싸 재렌더 대상 지정.
    //
    // 모든 행을 .tbl-row.col(라벨 위 / 입력 아래)로 둔다. 이름·연락처만 2열이었는데, 이 폼은
    // $j.Ui.Modal(variant:'devil') 안이라 글자크기 zoom 루트다 — '보통'도 1.2배라 레이아웃 폭이
    // 412px → 343px 로 줄고, 거기서 라벨 110px 을 빼면 입력칸 글자 자리가 71px 밖에 안 남아
    // 전화번호(약 104px)가 잘렸다. '아주 크게'에서는 값 칸이 표 밖으로 밀려 잘렸다.
    // (2026-10-06 상필 님 Z플립4 QA. 칸 폭 자체의 보정은 pages/_defense.scss 의 fs-keep)
    function renderBody() {
        const reasonRaw = S.notmeReason || '';
        const reasonLen = reasonRaw.trim().length;
        const reasonOk  = reasonLen >= 10;
        const reasonNeed = Math.max(0, 10 - reasonLen);
        const photoOk   = S.notmePhotos.length > 0;
        const nameOk    = (S.userName || '').trim().length >= 2;
        const agreed    = S.agreedPrivacy;
        const canSubmit = nameOk && reasonOk && photoOk && agreed;
        return `<div class="nm-form-root" style="display:flex;flex-direction:column;gap:16px">

          <div style="background:var(--Rl);border-radius:8px;padding:10px 12px">
            <p style="font-size:12px;color:var(--R);font-weight:600;line-height:1.6">⚠️ 위 내역이 본인 거래가 아닌 경우 아래에서 정정을 요청해 주세요.</p>
          </div>

          <div class="nm-card">
            <div style="margin-bottom:12px">
              <p class="sec-t-lg" style="display:flex;align-items:center;gap:8px">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7v6c0 5 4 8 10 9 6-1 10-4 10-9V7L12 2z" stroke="var(--R)" stroke-width="1.8" stroke-linejoin="round" fill="none"/><path d="M12 8v4M12 15v.5" stroke="var(--R)" stroke-width="1.8" stroke-linecap="round"/></svg>
                정정 요청서
              </p>
            </div>
            <div class="tbl">
              <div class="tbl-row col"><div class="tbl-tit">이름 <span style="color:var(--R);margin-left:2px">*</span></div><div class="tbl-val" style="flex-direction:column;align-items:stretch;padding:8px 14px"><input id="nm-name" class="inp" style="height:36px;border:1px solid var(--bdr);font-size:14px" placeholder="이름 입력" value="${Util.escapeHtml(S.userName)}" maxlength="20"></div></div>
              <div class="tbl-row col"><div class="tbl-tit">연락처</div><div class="tbl-val" style="flex-direction:column;align-items:stretch;padding:8px 14px"><input class="inp" style="height:36px;border:1px solid var(--bdr);font-size:14px" value="${Util.phoneNumber(S.authPhone || S.reportPhone)}" placeholder="본인인증한 번호" disabled></div></div>

              <div class="tbl-row col">
                <div class="tbl-tit">필요서류</div>
                <div class="tbl-val" style="flex-direction:column;align-items:stretch;gap:8px;padding:12px 14px">
                  <div style="font-size:14px;font-weight:700;color:var(--R);line-height:1.8">
                    <p>• 통신사 가입증명원</p>
                    <p>• (법인/사업자명의) 사업자등록증</p>
                  </div>
                  <div style="font-size:11px;color:var(--t2);line-height:1.7">
                    <p>※ 통신사 가입증명원은 통신사 고객센터·홈페이지에서 발급 가능합니다.</p>
                    <p>※ 서류는 <b>최근 1개월 이내</b> 발급분으로 제출해 주세요.</p>
                    <p>※ 주민번호 포함 서류는 <b>주민번호 뒷자리 마스킹</b> 후 제출해 주세요.</p>
                  </div>
                </div>
              </div>

              <div class="tbl-row col">
                <div class="tbl-tit">증빙 서류 <span style="color:var(--R);margin-left:2px">*</span> <span style="font-weight:400;color:var(--t2);font-size:11px;margin-left:4px">(최대 10장)</span></div>
                <div class="tbl-val" style="flex-direction:column;align-items:stretch;gap:8px;padding:12px 14px">
                  <div class="ph-grid">
                    ${S.notmePhotos.map((p, i) => `<div class="ph-thumb" data-pi="${i}" data-pg="notme"><img src="${Util.escapeHtml(p.url)}"><button class="ph-del" data-pi="${i}" data-pg="notme"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2l-6 6" stroke="var(--on-brand)" stroke-width="1.5" stroke-linecap="round"/></svg></button><div class="ph-zoom-ic" style="position:absolute;bottom:4px;left:4px;width:22px;height:22px;border-radius:6px;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;pointer-events:none"><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M1 9v4h4M13 5V1H9M1 13L5.5 8.5M13 1L8.5 5.5" stroke="var(--on-brand)" stroke-width="1.4" stroke-linecap="round"/></svg></div></div>`).join('')}
                    ${S.notmePhotos.length < 10 ? `<button type="button" class="ph-add" id="nm-ph-add-btn">
                      <div style="width:22px;height:22px;border-radius:11px;background:var(--R);display:flex;align-items:center;justify-content:center"><svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="var(--on-brand)" stroke-width="2" stroke-linecap="round"/></svg></div>
                      <span style="font-size:11px;color:var(--t2)">${S.notmePhotos.length}/10</span>
                    </button>` : ''}
                  </div>
                </div>
              </div>

              <div class="tbl-row col">
                <div class="tbl-tit">사유 <span style="color:var(--R);margin-left:2px">*</span> <span style="font-weight:400;color:var(--t2);font-size:11px;margin-left:4px">최소 10자, 최대 300자</span></div>
                <div class="tbl-val" style="flex-direction:column;align-items:stretch;padding:8px 14px">
                  <textarea class="inp" id="nm-reason" placeholder="본인이 아닌 사유를 작성해 주세요. (예: 명의가 잘못 등록되었습니다)" rows="4" maxlength="300" style="border:none;background:transparent;padding:8px 0">${Util.escapeHtml(S.notmeReason)}</textarea>
                  <div style="display:flex;justify-content:space-between;align-items:center;margin-top:2px;font-size:11px;line-height:1.4">
                    <span id="nm-reason-hint" style="color:var(--R);font-weight:600">${reasonOk ? '' : `${reasonNeed}자 더 입력해주세요`}</span>
                    <span id="nm-reason-count" style="color:${reasonOk ? 'var(--t3)' : 'var(--R)'};font-weight:${reasonOk ? '400' : '700'}">${reasonOk ? `${reasonRaw.length}/300` : `${reasonLen}/10`}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style="margin-top:16px;padding-top:12px;border-top:1px dashed var(--bdr)${S.skipAgree ? ';display:none' : ''}">
              <p class="chk-hint">항목을 눌러 내용을 끝까지 확인하면 동의됩니다.</p>
              <button type="button" class="chk-row chk-row--go" id="agree-row" style="padding:4px" aria-pressed="${agreed ? 'true' : 'false'}">
                <div class="chk-box${agreed ? ' on' : ''}">
                  ${agreed ? '<svg width="13" height="11" viewBox="0 0 14 12" fill="none"><path d="M1 6l4 4L13 1" stroke="var(--on-brand)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
                </div>
                <span style="font-size:13px;color:var(--t1)">개인정보 활용 동의 <b style="color:var(--R)">(필수)</b></span>
                <span class="chk-go" aria-hidden="true"><svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1 1l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
              </button>
            </div>

            <button id="nm-submit" ${canSubmit ? '' : 'disabled'} style="width:100%;height:52px;margin-top:16px;border-radius:14px;border:none;background:${canSubmit ? 'var(--grad)' : 'var(--bdr2)'};color:var(--on-brand);font-size:16px;font-weight:800;cursor:${canSubmit ? 'pointer' : 'not-allowed'};box-shadow:${canSubmit ? '0 6px 18px rgba(194,24,91,.28)' : 'none'};letter-spacing:-.3px">정정 요청 제출</button>
          </div>
        </div>`;
    }

    function bindBody() {
        $('#nm-name').off('input.nmf').on('input.nmf', function () { S.userName = $(this).val(); updateBtn(); });
        $('#nm-reason').off('input.nmf').on('input.nmf', function () {
            S.notmeReason = $(this).val();
            const raw = S.notmeReason;
            const len = raw.trim().length;
            const ok  = len >= 10;
            const need = Math.max(0, 10 - len);
            $('#nm-reason-count').text(ok ? `${raw.length}/300` : `${len}/10`)
                                 .css({ color: ok ? 'var(--t3)' : 'var(--R)', 'font-weight': ok ? '400' : '700' });
            $('#nm-reason-hint').text(ok ? '' : `${need}자 더 입력해주세요`);
            updateBtn();
        });
        // 동의 행 — 바로 토글하지 않는다. 동의 시트를 열어 끝까지 본 뒤 '동의합니다' 를 눌러야 켜진다(제보 화면과 같은 흐름).
        //   동의 시트가 폼 시트를 대신하므로(단일 인스턴스) 닫힐 때 openSheet() 로 폼을 복원한다 — 입력값은 S 에 남아 있다.
        $('#agree-row').off('click.nmf').on('click.nmf', function () {
            openPrivacyConsent(true, {
                agreed:  S.agreedPrivacy,
                onAgree: () => { S.agreedPrivacy = true; },
                onClose: () => openSheet(),
            });
        });
        $('#nm-ph-add-btn').off('click.nmf').on('click.nmf', function () { openPhotoUpload('defenseNotme', S.notmePhotos); });
        $(document).off('click.nmfdel').on('click.nmfdel', '.ph-del[data-pg="notme"]', function (e) {
            e.stopPropagation();
            S.notmePhotos.splice(+$(this).data('pi'), 1);
            rerender();
        });
        $('#nm-submit').off('click.nmf').on('click.nmf', function () {
            if ($(this).prop('disabled')) {
                if (!(S.userName || '').trim()) showToast('이름을 입력해 주세요');
                else if (S.notmePhotos.length === 0) showToast('증빙 서류를 첨부해 주세요');
                else if ((S.notmeReason || '').trim().length < 10) showToast('사유를 10자 이상 작성해 주세요');
                else if (!S.agreedPrivacy) showToast('개인정보 활용에 동의해 주세요');
                return;
            }
            confirmSubmit();
        });
    }

    function confirmSubmit() {
        showOv('정정 요청 제출 확인',
            `<div style="padding:18px 16px">
              <div style="text-align:center;padding:6px 0 18px">
                <div style="width:54px;height:54px;border-radius:27px;background:var(--Rl);display:flex;align-items:center;justify-content:center;margin:0 auto 14px">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 5v8M11 16v.5" stroke="var(--R)" stroke-width="2.5" stroke-linecap="round"/></svg>
                </div>
                <p style="font-size:16px;font-weight:700;color:var(--t1);margin-bottom:8px">제출 후 수정은 불가능합니다.</p>
                <p style="font-size:14px;color:var(--t2);line-height:1.6">정정 요청을 제출할까요?</p>
              </div>
              <div style="display:flex;gap:8px">
                <button class="nm-cancel" style="flex:1;height:48px;border-radius:14px;border:1.5px solid var(--bdr);background:var(--sf);cursor:pointer;font-size:15px;font-weight:700;color:var(--t1)">취소</button>
                <button class="nm-confirm" style="flex:1;height:48px;border-radius:14px;border:none;background:var(--grad);cursor:pointer;font-size:15px;font-weight:700;color:var(--on-brand)">제출</button>
              </div>
            </div>`,
            (sheet) => {
                sheet.find('.nm-cancel').on('click', () => openSheet());   // 폼 복원(입력값 S 보존)
                sheet.find('.nm-confirm').on('click', () => doSubmit());
            }
        );
    }

    function doSubmit() {
        const ld = showLoading('제출 중...');
        // 정정요청 제출 — extra_info={type:notme,...} 로 저장(서버). 성공 시 알림톡은 서버가 발송.
        $.ajax({ url: '/b/defense/ajax/notme/submit', type: 'POST', dataType: 'json', data: {
            name:    S.userName,
            phone:   S.authPhone,
            reason:  S.notmeReason,
            img_ids: S.notmePhotos.map(p => p.id),
            agree:   S.agreedPrivacy ? 1 : 0,
        } })
            .done((res) => {
                ld.remove();
                if (!res || !res.result) { showToast((res && res.message) || '제출에 실패했습니다.'); return; }
                showOv('정정 요청 접수 완료',
                    `<div style="padding:18px 16px">
                      <div style="text-align:center;padding:6px 0">
                        <div style="width:64px;height:64px;border-radius:32px;background:var(--Cg);display:flex;align-items:center;justify-content:center;margin:0 auto 14px">
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M7 14l4 4 10-10" stroke="var(--C)" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </div>
                        <p style="font-size:17px;font-weight:700;color:var(--t1);margin-bottom:8px">정정 요청이 접수되었습니다</p>
                        <p style="font-size:13px;color:var(--t2);line-height:1.7">관리자가 검토한 뒤 결과를<br>문자 또는 담당자 전화로 안내드립니다.</p>
                      </div>
                      <button class="nm-done" style="width:100%;height:48px;border-radius:14px;border:none;background:var(--grad);cursor:pointer;font-size:15px;font-weight:700;color:var(--on-brand);margin-top:8px">확인</button>
                    </div>`,
                    // 이 시트는 showOv(= $j.Ui.Modal)다. 옛 BS 시절의 $j.Ui.BS.close() 가 남아 있어 아무것도 안 닫혔고,
                    // 뒤 화면만 접수 완료로 바뀐 채 시트가 그대로 떠 있었다(2026-10-07 상필 님 QA). showOv 가 준 remove 로 닫는다.
                    (sheet) => sheet.find('.nm-done').on('click', () => { sheet.remove(); setActiveForm(null); go('notme_done'); })
                );
            })
            .fail(() => { ld.remove(); showToast('제출 중 오류가 발생했습니다.'); });
    }

    function rerender() {
        if (!_sheet) return;
        const $root = _sheet.find('.nm-form-root');
        if (!$root.length) return;
        $root.replaceWith(renderBody());
        bindBody();
    }

    function openSheet() {
        setActiveForm('notme');
        showOv('정정 요청서', renderBody(), (sheet) => { _sheet = sheet; bindBody(); });
    }

    return { openSheet, rerender };
}
